/* Direct-add (no popups) + compact footer cart bar.
 *
 * - [data-kp-direct-add] posts the single unambiguous variant to /cart/add.js.
 * - Count and contents always come from Shopify's cart response, never local state.
 * - The bar also opens after a product-page add. Dawn's product form publishes
 *   PUB_SUB_EVENTS.cartUpdate, so we subscribe to it instead of watching the
 *   header DOM (Dawn replaces that subtree wholesale, which detaches any
 *   observed node and kills the observer).
 * - Every visible label lives in snippets/kp-cart-bar.liquid and is theme-setting
 *   driven; this file only fills in the product name and the number.
 */
(() => {
  const bar = document.getElementById('kp-cart-bar');
  if (!bar) return;
  const barEnabled = bar.dataset.enabled === 'true';
  const FLAG = 'kp-cart-bar-shown';
  const LAST = 'kp-cart-bar-last';
  const messageEl = bar.querySelector('[data-kp-cart-bar-message]');
  const countEl = bar.querySelector('[data-kp-cart-bar-count]');
  const statusEl = bar.querySelector('[data-kp-cart-bar-status]');
  const messageTemplate = messageEl ? messageEl.textContent : '';
  if (messageEl) messageEl.dataset.kpCartBarTemplate = messageTemplate;

  const countLabelOne = bar.dataset.cartCountLabelOne || 'Shopping cart, [count] item';
  const countLabelOther = bar.dataset.cartCountLabelOther || 'Shopping cart, [count] items';
  const countLabel = (count) =>
    (Number(count) === 1 ? countLabelOne : countLabelOther).replace('[count]', String(count));
  const addingLabel = bar.dataset.addingLabel || 'Adding…';
  const errorRefreshLabel = bar.dataset.errorRefreshLabel || 'Added to cart, but count could not refresh. View cart to confirm.';
  const errorAddLabel = bar.dataset.errorAddLabel || 'Could not add to cart. Please try again.';
  const announceTemplate = statusEl ? statusEl.dataset.kpAnnounceTemplate || '' : '';

  let seq = 0;

  const setCount = (count) => {
    if (countEl) countEl.textContent = String(count);

    const bubble = document.getElementById('cart-icon-bubble');
    if (bubble) {
      let badge = bubble.querySelector('.cart-count-bubble');
      if (count > 0 && !badge) {
        badge = document.createElement('div');
        badge.className = 'cart-count-bubble';
        const num = document.createElement('span');
        num.setAttribute('aria-hidden', 'true');
        const vh = document.createElement('span');
        vh.className = 'visually-hidden';
        badge.append(num, vh);
        bubble.append(badge);
      }
      if (badge) {
        const num = badge.querySelector('[aria-hidden]');
        // Keep the number visible at 100+ instead of blanking it.
        if (num) num.textContent = count < 100 ? String(count) : '99+';
        const vh = badge.querySelector('.visually-hidden');
        if (vh) vh.textContent = countLabel(count);
        badge.hidden = count < 1;
      }
      // The two cart glyphs are both in the markup; the state attribute picks one.
      bubble.dataset.cartState = count > 0 ? 'full' : 'empty';
    }
    // The mobile dock mirrors the header bubble itself (assets/kp-mobile-dock.js).
  };

  /* The compare tray and this bar are both fixed to the bottom, so the bar has
   * to clear the tray's rendered box. Adding offsetHeight to the tray's CSS
   * bottom under-reads while the tray is still growing its chip list, which left
   * the two overlapping on phones, so measure the box itself and re-measure when
   * the tray changes or finishes sliding in. */
  const layoutBars = () => {
    const tray = document.getElementById('kp-compare-tray');
    if (!tray || !tray.classList.contains('is-open')) {
      bar.style.bottom = '';
      return;
    }
    const stacked = window.innerHeight - tray.getBoundingClientRect().top + 8;
    const ceiling = window.innerHeight - bar.offsetHeight - 8;
    bar.style.bottom = `${Math.round(Math.max(0, Math.min(stacked, ceiling)))}px`;
  };

  const show = (title, count) => {
    if (!barEnabled) {
      setCount(count);
      return;
    }
    if (messageEl) {
      const template = messageEl.dataset.kpCartBarTemplate || messageTemplate;
      messageEl.textContent = title
        ? template.replace('[product]', title)
        : template.replace('[product]', '').replace(/\s{2,}/g, ' ').trim();
    }
    setCount(count);
    bar.hidden = false;
    bar.inert = false;
    layoutBars();
  };

  const hide = () => {
    bar.hidden = true;
    bar.inert = true;
    bar.style.bottom = '';
    try { sessionStorage.removeItem(FLAG); sessionStorage.removeItem(LAST); } catch (_) {}
  };

  const readCart = async () => {
    try {
      const res = await fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } });
      if (!res.ok) return null;
      return await res.json();
    } catch (_) { return null; }
  };

  // Shopify keeps a re-added variant in its original line, so the newest line is
  // the one with the latest added_at rather than the last array entry.
  const newestTitle = (cart, variantId) => {
    const items = cart && cart.items ? cart.items : [];
    if (!items.length) return '';
    const id = Number(variantId);
    const exact = id ? items.find((item) => Number(item.id) === id) : null;
    if (exact) return exact.product_title || '';
    let pick = items[items.length - 1];
    items.forEach((item) => {
      if (new Date(item.added_at || 0) > new Date(pick.added_at || 0)) pick = item;
    });
    return pick.product_title || '';
  };

  const mark = (title) => {
    if (!barEnabled) return;
    try {
      sessionStorage.setItem(FLAG, '1');
      sessionStorage.setItem(LAST, title || '');
    } catch (_) {}
  };

  const refresh = async (title) => {
    const ticket = ++seq;
    const cart = await readCart();
    if (!cart) return null;
    // Another add may already be refreshing the same cart. The older request
    // still succeeded; skip its stale UI write without reporting a false error.
    if (ticket !== seq) return cart;
    setCount(cart.item_count);
    if (cart.item_count < 1) { hide(); return cart; }
    mark(title);
    show(title || newestTitle(cart), cart.item_count);
    return cart;
  };

  document.addEventListener('click', async (event) => {
    const btn = event.target.closest?.('[data-kp-direct-add]');
    if (!btn || btn.disabled) return;
    const label = btn.querySelector('[data-kp-direct-label]');
    const err = btn.querySelector('[data-kp-direct-error]');
    const original = label ? label.textContent : '';
    const title = btn.dataset.productTitle || '';
    btn.disabled = true;
    btn.classList.add('is-adding');
    if (label) label.textContent = addingLabel;
    if (err) err.hidden = true;
    let added = false;
    try {
      const add = await fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(btn.dataset.variantId), quantity: 1 }] }),
      });
      if (!add.ok) throw new Error(`add failed (${add.status})`);
      await add.json();
      added = true;
      const cart = await refresh(title);
      if (!cart) throw new Error('cart refresh failed');
      if (statusEl) statusEl.textContent = announceTemplate.replace('[product]', title);
    } catch (_) {
      if (err) {
        err.textContent = added ? errorRefreshLabel : errorAddLabel;
        err.hidden = false;
      } else {
        btn.setAttribute('aria-label', added ? errorRefreshLabel : errorAddLabel);
      }
      if (added) refresh();
    } finally {
      btn.disabled = false;
      btn.classList.remove('is-adding');
      if (label) label.textContent = original;
    }
  });

  const cont = bar.querySelector('[data-kp-cart-bar-continue]');
  if (cont) cont.addEventListener('click', hide);

  /* Dawn publishes PUB_SUB_EVENTS.cartUpdate on every cart change, but the
   * payload is only a Cart Ajax object for some sources: the product form
   * requests section HTML from /cart/add, so its `cartData` has no item_count.
   * Reading 0 out of it would blank and hide the bar right after a successful
   * add, so anything that is not a cart object falls back to /cart.js. */
  const isCartPayload = (cart) =>
    !!cart && Array.isArray(cart.items) && Number.isFinite(Number(cart.item_count));

  const applyCartData = (cart, opts) => {
    if (!cart) return;
    const count = Number(cart.item_count) || 0;
    setCount(count);
    if (count < 1) { hide(); return; }
    if (opts && opts.show) {
      const title = opts.title || newestTitle(cart, opts.variantId);
      mark(title);
      show(title, count);
      if (statusEl) statusEl.textContent = announceTemplate.replace('[product]', title);
    } else if (!bar.hidden) {
      // The bar is already on screen - keep it in sync without re-announcing.
      show(newestTitle(cart, opts && opts.variantId), count);
    }
  };

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, async (event) => {
      const isAdd = !!event && event.source === 'product-form';
      const variantId = event && event.productVariantId;
      const data = event && event.cartData;
      if (isCartPayload(data)) {
        applyCartData(data, { show: isAdd, variantId });
        return;
      }
      const cart = await readCart();
      if (cart) applyCartData(cart, { show: isAdd, variantId });
    });
  }

  readCart().then((cart) => {
    if (!cart) return;
    setCount(cart.item_count);
    if (cart.item_count < 1) {
      try { sessionStorage.removeItem(FLAG); sessionStorage.removeItem(LAST); } catch (_) {}
    } else {
      let flag = null;
      let last = '';
      try {
        flag = sessionStorage.getItem(FLAG);
        last = sessionStorage.getItem(LAST) || '';
      } catch (_) {}
      if (flag) show(last || newestTitle(cart), cart.item_count);
    }
  });

  const tray = document.getElementById('kp-compare-tray');
  if (tray && typeof MutationObserver === 'function') {
    new MutationObserver(layoutBars).observe(tray, {
      attributes: true,
      attributeFilter: ['class'],
      childList: true,
      subtree: true,
      characterData: true,
    });
    tray.addEventListener('transitionend', layoutBars);
  }
  window.addEventListener('resize', layoutBars);
})();
